import TYPES from '@/types';
import Vue from 'vue';
import { currencyFormat } from '@/vue-app/utils/currency';
import { parseCurrencyToNumber } from '@/vue-app/utils/parse-currency-to-number';

// Application
import {
  GetAllianzAccountQuery,
} from '@/modules/my-investment/allianz/allianz-account/application/queries';
import GetSearchByCustomerQuery
  from '@/modules/flagship/investor-goal/search-by-customer/application/queries/get-search-by-customer-query';

// Domain
import {
  LinkGoalsWealthStateManager,
} from '@/modules/flagship/investor-goal/link-sowos-wealth/domain/state/link-goals-wealth-state-manager';
import {
  StateManager as ContributionsStateManager,
} from '@/modules/my-investment/allianz/recurring-contributions/domain/state/state-manager';
import {
  SearchByCustomerDto,
} from '@/modules/flagship/investor-goal/search-by-customer/domain/dtos/search-by-customer-dto';
import {
  InvestorGoal,
} from '@/modules/flagship/investor-goal/link-sowos-wealth/domain/dtos/investor-goals-dto';
import Inject from '@/modules/shared/domain/di/inject';
import Translator from '@/modules/shared/domain/i18n/translator';
import { Values } from '@/modules/shared/domain/i18n/types';
import { MessageNotifier } from '@/modules/shared/domain/notifiers/message_notifier';
import {
  GoalForLinkDto,
} from '@/modules/flagship/investor-goal/link-sowos-wealth/domain/dtos/goal-for-link-dto';

type ActiveRecurrentAmountAmongGoals = {
  label: string;
  recurrent_amount: number;
  tooltip_text: string;
}

export default class StepConfigureContributionsWealthViewModel {
  @Inject(TYPES.GET_ALLIANZ_ACCOUNT_QUERY)
  private readonly get_allianz_account_query!: GetAllianzAccountQuery;

  @Inject(TYPES.GET_SEARCH_BY_CUSTOMER_QUERY)
  private readonly get_search_by_customer_query!: GetSearchByCustomerQuery;

  @Inject(TYPES.LINK_GOALS_WEALTH_STATE_MANAGER)
  readonly link_goals_wealth_state_manager!: LinkGoalsWealthStateManager;

  @Inject(TYPES.ALLIANZ_RECURRING_CONTRIBUTIONS_STATE_MANAGER)
  private readonly contributions_state_manager!:
    ContributionsStateManager;

  @Inject(TYPES.I18N)
  readonly translator!: Translator;

  @Inject(TYPES.NOTIFIER)
  private readonly message_notifier!: MessageNotifier;

  private readonly view!: Vue;

  readonly i18n_namespace = 'components.flagship.flagship-link-goals.step-configure-contributions-wealth';

  private readonly customer_id = sessionStorage.getItem('user_id');

  private readonly error_allianz_account_not_exists = `Error customer_id:${this.customer_id}, not exists`;

  link_goals_wealth_state = this.link_goals_wealth_state_manager.state;

  contributions_state = this.contributions_state_manager.state;

  private search_by_customer_dto: SearchByCustomerDto = {
    reload: true,
    associated_product_id: '',
    is_active: true,
  };

  investor_goals: Array<InvestorGoal> = [];

  goals_selected: Array<string> = [];

  is_disabled = false;

  recurrent_assigned_amount = 0;

  max_value_slider = 50000;

  min_value_slider = 500;

  maximum_required_value = 100000;

  new_recurrent_amount = '0';

  message = '';

  class = '';

  has_recurring_contributions = true;

  slider_step = 50;

  has_active_goal = false;

  active_recurrent_amount_among_goals: Array<ActiveRecurrentAmountAmongGoals> = [];

  recurrent_unassigned_contribution = 0;

  min_value_required_by_goals: Array<number> = []

  define_contribution_later = false;

  show_maximum_amount_exceeded_error = false;

  public constructor(view: Vue) {
    this.view = view;
  }

  get disabled_btn_next() {
    return this.is_disabled || this.link_goals_wealth_state.is_loading
      || parseCurrencyToNumber(this.new_recurrent_amount) > 100000;
  }

  get has_active_goal_with_contributions() {
    return this.contributions_state.item.contributions.length > 0
      && this.contributions_state.item.contributions[0].distributions.length > 0;
  }

  // eslint-disable-next-line max-len
  initialize = async () => {
    this.link_goals_wealth_state.is_loading = true;
    await this.loadRecurrentAssignedAmount();
    await this.loadActiveGoals();
    await this.loadAssociatedProduct();
    this.checkIfCustomerHasRecurringContributions();
    this.setRecurrentUnassignedContributionToZeroIfCustomerHasNoActiveGoals();
    this.link_goals_wealth_state.is_loading = false;
  }

  translate = (message: string, values?: Values) => this.translator.translate(`${this.i18n_namespace}.${message}`, values);

  getAmountFormatted(amount: number) {
    return currencyFormat(amount);
  }

  get recurrent_assigned_amount_formatted() {
    return currencyFormat(this.recurrent_assigned_amount);
  }

  loadActiveGoals = async () => {
    try {
      this.search_by_customer_dto.associated_product_id = this.link_goals_wealth_state
        .associated_product_id;
      const active_goals = await this.get_search_by_customer_query
        .execute(this.search_by_customer_dto);
      if (active_goals.length) {
        this.has_active_goal = true;
        active_goals.forEach((goal) => {
          if (goal.recurrent_assigned_amount > 0) {
            this.active_recurrent_amount_among_goals.push({
              label: (goal.custom_investor_goal) ? goal.custom_investor_goal.goal_name
                : goal.investment_goal_type.label,
              recurrent_amount: goal.recurrent_assigned_amount,
              tooltip_text: (goal.custom_investor_goal) ? this
                .translate('tooltip_custom_goal') : this.translate('tooltip_retirement_goal'),
            });
            this.calculateRecurrentUnassignedContribution(goal.recurrent_assigned_amount);
          }
        });
        this.checkIfRecurrentUnassignedContributionIsMoreThanZero();
      }
    } catch (error) {
      this.message_notifier.showErrorNotification(this.translate('errors.load_active_goals'));
    }
  }

  addProduct = (selected_goal: GoalForLinkDto, index: number) => {
    const min_value_required = selected_goal.recommend_amount === 0 ? 0 : this.min_value_slider;
    const monthly_required_amount_rounded = Math.ceil(selected_goal.recommend_amount);
    this.link_goals_wealth_state.selected_goals[index].recurrent_assigned_amount = (
      monthly_required_amount_rounded < 500 && monthly_required_amount_rounded > 0
        ? this.min_value_slider : monthly_required_amount_rounded
    );
    this.link_goals_wealth_state.selected_goals[index].recurrent_assigned_amount_field = this
      .link_goals_wealth_state.selected_goals[index].recurrent_assigned_amount.toString();
    this.min_value_required_by_goals.push(min_value_required);
  }

  getTotalMinValue = (recurrent_assigned_amount: number, monthly_required_amount: number) => {
    if (recurrent_assigned_amount > 0) {
      this.is_disabled = false;
      return recurrent_assigned_amount;
    }
    return Math.ceil(monthly_required_amount);
  }

  loadAssociatedProduct = async () => {
    try {
      let total_min_value = 0;
      this.link_goals_wealth_state.selected_goals.forEach((selected_goal, index) => {
        this.addProduct(selected_goal, index);
        total_min_value += this.getTotalMinValue(
          selected_goal.recurrent_assigned_amount, selected_goal.recommend_amount,
        );
      });
      if (this.has_active_goal) {
        total_min_value += this.recurrent_assigned_amount;
      }
      this.new_recurrent_amount = total_min_value.toString();
    } catch {
      this.message_notifier.showErrorNotification(this.translate('errors.get_associated_product'));
    }
  }

  loadRecurrentAssignedAmount = async () => {
    try {
      // eslint-disable-next-line max-len
      const { amount } = await this.get_allianz_account_query.execute({ customer_id: this.customer_id });
      this.recurrent_assigned_amount = amount || 0;
      this.recurrent_unassigned_contribution = this.recurrent_assigned_amount;
    } catch (error) {
      if (error.message !== this.error_allianz_account_not_exists) {
        this.message_notifier.showErrorNotification(this.translate('errors.get_recurrent_assigned_amount'));
      }
    }
  }

  calculateRecurrentUnassignedContribution = (recurrent_amount: number) => {
    this.recurrent_unassigned_contribution -= recurrent_amount;
  }

  calculateBalanceAmount = (index: number) => {
    // eslint-disable-next-line max-len
    const minimum_required_value = this.link_goals_wealth_state.selected_goals[index]
      .recommend_amount === 0
      ? this.link_goals_wealth_state.selected_goals[index].recommend_amount : this.min_value_slider;
    let active_goal_amount = (this.link_goals_wealth_state.selected_goals[index].recurrent_assigned_amount_field !== '$0 MXN')
      // eslint-disable-next-line max-len
      ? parseCurrencyToNumber(this.link_goals_wealth_state.selected_goals[index].recurrent_assigned_amount_field)
      : minimum_required_value;
    // eslint-disable-next-line max-len
    active_goal_amount = (active_goal_amount < minimum_required_value) ? minimum_required_value : active_goal_amount;
    // eslint-disable-next-line max-len
    this.link_goals_wealth_state.selected_goals[index].recurrent_assigned_amount = active_goal_amount < this.max_value_slider
      ? active_goal_amount : this.max_value_slider;
    this.link_goals_wealth_state.selected_goals[index]
      .recurrent_assigned_amount_field = active_goal_amount
      < this.max_value_slider ? (active_goal_amount).toString()
        : (this.max_value_slider).toString();

    const amount_sliders = this.link_goals_wealth_state.selected_goals.map((obj) => (
      parseCurrencyToNumber(obj.recurrent_assigned_amount_field)
    ));
    let total_amount_sliders = amount_sliders.reduce(
      (total, currentValue) => total + currentValue, 0,
    );
    if (this.has_active_goal) {
      total_amount_sliders += this.recurrent_assigned_amount;
    }
    this.is_disabled = total_amount_sliders < minimum_required_value
      || total_amount_sliders > this.maximum_required_value;

    this.new_recurrent_amount = total_amount_sliders > 0
      ? (total_amount_sliders).toString() : this.recurrent_assigned_amount.toString();

    this.setCustomMessage(
      Number(active_goal_amount),
      this.link_goals_wealth_state.selected_goals[index].recommend_amount,
      this.link_goals_wealth_state.selected_goals[index].investor_goal_id,
    );
    this.show_maximum_amount_exceeded_error = total_amount_sliders > this.maximum_required_value;
  }

  // eslint-disable-next-line max-len
  setCustomMessage = (currentAmount: number, recommended_amount: number, investor_goal_id: string) => {
    if (currentAmount < recommended_amount) {
      this.message = this.translate('message_more_time');
      this.class = 'red--text';
    } else if (currentAmount === recommended_amount) {
      this.message = this.translate('message_enough_time');
      this.class = 'sky-blue--text';
    } else {
      this.message = this.translate('message_less_time');
      this.class = 'secondary--text';
    }
    // eslint-disable-next-line max-len
    const index = this.link_goals_wealth_state.selected_goals.findIndex(
      (item) => item.investor_goal_id === investor_goal_id,
    );
    this.link_goals_wealth_state.selected_goals[index].message = this.message;
    this.link_goals_wealth_state.selected_goals[index].class = this.class;
  }

  checkIfCustomerHasRecurringContributions = () => {
    if (!this.recurrent_assigned_amount) {
      this.has_recurring_contributions = false;
    }
  }

  setRecurrentUnassignedContributionToZeroIfCustomerHasNoActiveGoals = () => {
    if (!this.has_active_goal) {
      this.recurrent_unassigned_contribution = 0;
    }
  }

  checkIfRecurrentUnassignedContributionIsMoreThanZero = () => {
    if (this.recurrent_unassigned_contribution > 0) {
      this.active_recurrent_amount_among_goals.push({
        label: this.translate('contribution_unassigned_balance'),
        recurrent_amount: this.recurrent_unassigned_contribution,
        tooltip_text: this.translate('tooltip_unassigned_balance'),
      });
    }
  }

  defineContributionLaterChange = () => {
    if (this.define_contribution_later) {
      // eslint-disable-next-line no-plusplus
      for (let i = 0; i < this.link_goals_wealth_state.selected_goals.length; i++) {
        this.link_goals_wealth_state.selected_goals[i].recurrent_assigned_amount_field = '0.00';
        this.link_goals_wealth_state.selected_goals[i].recurrent_assigned_amount = 0;
      }
      this.new_recurrent_amount = this.recurrent_assigned_amount.toString();
    } else {
      this.is_disabled = true;
    }
  }

  sendNewRecurrentAssignedAmount = () => {
    const new_recurrent_amount = parseCurrencyToNumber(this.new_recurrent_amount);
    this.link_goals_wealth_state.define_contribution_later = this.define_contribution_later;
    this.link_goals_wealth_state.total_goals_contribution = this
      .link_goals_wealth_state.selected_goals.reduce(
        (total, selected_goal) => total + selected_goal.recurrent_assigned_amount, 0,
      );
    if (this.define_contribution_later) {
      this.view.$emit('linkGoals');
    } else {
      this.view.$emit('nextStep');
      if (new_recurrent_amount <= this.max_value_slider
        && this.contributions_state.item.contributions.length < 2) {
        this.view.$emit('nextStep');
      }
    }
  }

  goToPrevStep = () => {
    this.view.$emit('prevStep');
  }
}
